/**
 * useGameChallenge Hook
 * Manage game challenges (sending, accepting, declining)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  GameChallenge,
  CreateChallengeParams,
  GameChallengeStatus,
  SessionMode,
} from "../types";
import { createInAppNotification, scheduleLocalNotification } from "@/lib/notifications";

interface UseGameChallengeReturn {
  /** Pending challenges received by current user */
  receivedChallenges: GameChallenge[];
  /** Pending challenges sent by current user */
  sentChallenges: GameChallenge[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Send a new challenge */
  sendChallenge: (params: CreateChallengeParams) => Promise<GameChallenge | null>;
  /** Accept a challenge */
  acceptChallenge: (challengeId: string) => Promise<string | null>;
  /** Decline a challenge */
  declineChallenge: (challengeId: string) => Promise<void>;
  /** Cancel a sent challenge */
  cancelChallenge: (challengeId: string) => Promise<void>;
  /** Refetch challenges */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage game challenges
 */
export const useGameChallenge = (): UseGameChallengeReturn => {
  const { activeProfile, activeWeekend, userTeam } = useAuth();
  const userId = activeProfile?.id;

  const [receivedChallenges, setReceivedChallenges] = useState<GameChallenge[]>([]);
  const [sentChallenges, setSentChallenges] = useState<GameChallenge[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /**
   * Fetch all pending challenges
   */
  const fetchChallenges = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      // Fetch received challenges
      const { data: received, error: receivedError } = await supabase
        .from("game_challenges")
        .select(`
          *,
          challenger:profiles!game_challenges_challenger_id_fkey(id, full_name, avatar_url),
          game:game_definitions!game_challenges_game_slug_fkey(*)
        `)
        .eq("challenged_id", userId)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false });

      if (receivedError) throw receivedError;
      setReceivedChallenges(received as GameChallenge[]);

      // Fetch sent challenges
      const { data: sent, error: sentError } = await supabase
        .from("game_challenges")
        .select(`
          *,
          challenged:profiles!game_challenges_challenged_id_fkey(id, full_name, avatar_url),
          game:game_definitions!game_challenges_game_slug_fkey(*)
        `)
        .eq("challenger_id", userId)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false });

      if (sentError) throw sentError;
      setSentChallenges(sent as GameChallenge[]);
    } catch (err) {
      console.error("Error fetching challenges:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch challenges"));
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Subscribe to challenge changes via Realtime
   */
  const subscribeToChanges = useCallback(() => {
    if (!userId) return;

    // Unsubscribe from previous subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
    }

    subscriptionRef.current = supabase
      .channel(`game_challenges:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_challenges",
          filter: `challenged_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === "INSERT") {
            // New challenge received - refetch to get joined data
            fetchChallenges();
          } else if (payload.eventType === "UPDATE" || payload.eventType === "DELETE") {
            fetchChallenges();
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_challenges",
          filter: `challenger_id=eq.${userId}`,
        },
        (payload) => {
          // Challenge we sent was updated (accepted/declined)
          fetchChallenges();
        }
      )
      .subscribe();
  }, [userId, fetchChallenges]);

  // Initial fetch and subscription
  useEffect(() => {
    if (userId) {
      fetchChallenges();
      subscribeToChanges();
    }

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [userId, fetchChallenges, subscribeToChanges]);

  /**
   * Send a new challenge
   */
  const sendChallenge = useCallback(
    async (params: CreateChallengeParams): Promise<GameChallenge | null> => {
      if (!userId || !activeProfile) {
        setError(new Error("User not authenticated"));
        return null;
      }

      try {
        const challengeData = {
          game_slug: params.game_slug,
          challenger_id: userId,
          challenged_id: params.challenged_id,
          weekend_id: params.mode === "weekend" ? activeWeekend?.id : null,
          mode: params.mode,
          status: "pending" as GameChallengeStatus,
          game_config: params.game_config || {},
          message: params.message || null,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        };

        const { data, error: insertError } = await supabase
          .from("game_challenges")
          .insert(challengeData)
          .select(`
            *,
            challenged:profiles!game_challenges_challenged_id_fkey(id, full_name, avatar_url),
            game:game_definitions!game_challenges_game_slug_fkey(*)
          `)
          .single();

        if (insertError) throw insertError;

        const challenge = data as GameChallenge;

        // Send notification to challenged user
        await createInAppNotification({
          userId: params.challenged_id,
          title: `🎮 Uitdaging van ${activeProfile.full_name}!`,
          body: `${activeProfile.full_name} daagt je uit voor ${challenge.game?.name || params.game_slug}${params.message ? `: "${params.message}"` : ""}`,
          type: "game_challenge",
          actionUrl: `/games/uitdagingen`,
          actionData: { challenge_id: challenge.id },
          senderId: userId,
          weekendId: activeWeekend?.id,
          lottieAnimation: "party",
        });

        // Send push notification
        const { data: pushTokens } = await supabase
          .from("push_tokens")
          .select("token")
          .eq("user_id", params.challenged_id)
          .eq("is_active", true);

        if (pushTokens && pushTokens.length > 0) {
          await scheduleLocalNotification({
            title: `🎮 Uitdaging!`,
            body: `${activeProfile.full_name} daagt je uit voor ${challenge.game?.name || params.game_slug}`,
            data: { type: "game_challenge", challenge_id: challenge.id },
          });
        }

        // Update local state
        setSentChallenges((prev) => [challenge, ...prev]);

        return challenge;
      } catch (err) {
        console.error("Error sending challenge:", err);
        setError(err instanceof Error ? err : new Error("Failed to send challenge"));
        return null;
      }
    },
    [userId, activeProfile, activeWeekend]
  );

  /**
   * Accept a challenge and create a game session
   */
  const acceptChallenge = useCallback(
    async (challengeId: string): Promise<string | null> => {
      if (!userId || !activeProfile) {
        setError(new Error("User not authenticated"));
        return null;
      }

      try {
        // Get the challenge
        const { data: challenge, error: fetchError } = await supabase
          .from("game_challenges")
          .select(`
            *,
            challenger:profiles!game_challenges_challenger_id_fkey(id, full_name, avatar_url),
            game:game_definitions!game_challenges_game_slug_fkey(*)
          `)
          .eq("id", challengeId)
          .single();

        if (fetchError) throw fetchError;

        // Get challenger's team
        const { data: challengerTeamMember } = await supabase
          .from("team_members")
          .select("team_id")
          .eq("user_id", challenge.challenger_id)
          .single();

        // Create game session with both participants
        const sessionData = {
          game_slug: challenge.game_slug,
          weekend_id: challenge.mode === "weekend" ? challenge.weekend_id : null,
          mode: challenge.mode,
          session_type: "versus",
          status: "active",
          created_by: challenge.challenger_id,
          started_at: new Date().toISOString(),
          participants: [
            {
              user_id: challenge.challenger_id,
              team_id: challengerTeamMember?.team_id || null,
              display_name: challenge.challenger?.full_name || "Player 1",
              avatar_url: challenge.challenger?.avatar_url || null,
              role: "host",
              score: 0,
              joined_at: new Date().toISOString(),
            },
            {
              user_id: userId,
              team_id: userTeam?.id || null,
              display_name: activeProfile.full_name || "Player 2",
              avatar_url: activeProfile.avatar_url || null,
              role: "player",
              score: 0,
              joined_at: new Date().toISOString(),
            },
          ],
          game_state: {
            config: challenge.game_config,
          },
        };

        const { data: session, error: sessionError } = await supabase
          .from("game_sessions")
          .insert(sessionData)
          .select()
          .single();

        if (sessionError) throw sessionError;

        // Update challenge status
        const { error: updateError } = await supabase
          .from("game_challenges")
          .update({
            status: "accepted",
            session_id: session.id,
            responded_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (updateError) throw updateError;

        // Notify challenger that challenge was accepted
        await createInAppNotification({
          userId: challenge.challenger_id,
          title: `✅ Uitdaging geaccepteerd!`,
          body: `${activeProfile.full_name} heeft je uitdaging voor ${challenge.game?.name} geaccepteerd!`,
          type: "game_challenge_accepted",
          actionUrl: `/games/${challenge.game_slug}/play/${session.id}`,
          actionData: { session_id: session.id },
          senderId: userId,
          lottieAnimation: "confetti",
        });

        // Update local state
        setReceivedChallenges((prev) => prev.filter((c) => c.id !== challengeId));

        return session.id;
      } catch (err) {
        console.error("Error accepting challenge:", err);
        setError(err instanceof Error ? err : new Error("Failed to accept challenge"));
        return null;
      }
    },
    [userId, activeProfile, userTeam]
  );

  /**
   * Decline a challenge
   */
  const declineChallenge = useCallback(
    async (challengeId: string) => {
      if (!userId) return;

      try {
        const { data: challenge } = await supabase
          .from("game_challenges")
          .select("*, challenger:profiles!game_challenges_challenger_id_fkey(full_name)")
          .eq("id", challengeId)
          .single();

        const { error } = await supabase
          .from("game_challenges")
          .update({
            status: "declined",
            responded_at: new Date().toISOString(),
          })
          .eq("id", challengeId);

        if (error) throw error;

        // Notify challenger (in-app only, no push for declines)
        if (challenge) {
          await createInAppNotification({
            userId: challenge.challenger_id,
            title: `Uitdaging afgewezen`,
            body: `${activeProfile?.full_name} heeft de uitdaging afgewezen`,
            type: "game_challenge_declined",
            senderId: userId,
          });
        }

        // Update local state
        setReceivedChallenges((prev) => prev.filter((c) => c.id !== challengeId));
      } catch (err) {
        console.error("Error declining challenge:", err);
        setError(err instanceof Error ? err : new Error("Failed to decline challenge"));
      }
    },
    [userId, activeProfile]
  );

  /**
   * Cancel a sent challenge
   */
  const cancelChallenge = useCallback(
    async (challengeId: string) => {
      if (!userId) return;

      try {
        const { error } = await supabase
          .from("game_challenges")
          .update({ status: "cancelled" })
          .eq("id", challengeId)
          .eq("challenger_id", userId);

        if (error) throw error;

        // Update local state
        setSentChallenges((prev) => prev.filter((c) => c.id !== challengeId));
      } catch (err) {
        console.error("Error cancelling challenge:", err);
        setError(err instanceof Error ? err : new Error("Failed to cancel challenge"));
      }
    },
    [userId]
  );

  return {
    receivedChallenges,
    sentChallenges,
    isLoading,
    error,
    sendChallenge,
    acceptChallenge,
    declineChallenge,
    cancelChallenge,
    refetch: fetchChallenges,
  };
};

